import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import CarouselControls from './CarouselControls';
import { caseStudies, getHeroImageSrc } from '../data/caseStudies';
import { sideProjects } from '../data/siteData';
import { updateCarouselDots } from '../utils/carouselDots';
import './ThumbnailCarousel.css';

const items = [
  ...caseStudies.map((study) => ({
    key: `case-${study.id}`,
    image: getHeroImageSrc(study),
    title: study.title,
    url: `/work/${study.id}`,
  })),
  ...sideProjects.map((project, i) => ({
    key: `side-${i}`,
    image: project.image,
    imagePosition: project.imagePosition,
    title: project.title,
    url: project.url,
  })),
];

const AUTO_ADVANCE_MS = 5000;

const ThumbnailCarousel = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapperRef = useRef(null);
  const scrollRef = useRef(null);
  const dotRefs = useRef([]);
  // Last known real pointer position (window-level, not onMouseEnter/Leave —
  // see the auto-advance effect below for why).
  const pointerRef = useRef({ x: -1, y: -1 });

  const updateDots = useCallback(() => {
    const container = scrollRef.current;
    if (!container || !container.offsetWidth) return;

    const raw = container.scrollLeft / container.offsetWidth;
    const fromIdx = Math.max(0, Math.min(items.length - 1, Math.floor(raw)));
    const toIdx = Math.min(fromIdx + 1, items.length - 1);
    const dotT = fromIdx === toIdx ? 0 : Math.max(0, Math.min(1, raw - fromIdx));

    updateCarouselDots(dotRefs, fromIdx, toIdx, dotT);

    setActiveIndex(Math.max(0, Math.min(items.length - 1, Math.round(raw))));
  }, []);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    container.addEventListener('scroll', updateDots, { passive: true });
    updateDots();
    return () => container.removeEventListener('scroll', updateDots);
  }, [updateDots]);

  const scrollToIndex = (index) => {
    const clamped = Math.max(0, Math.min(items.length - 1, index));
    const container = scrollRef.current;
    if (!container) return;
    container.scrollTo({ left: clamped * container.offsetWidth, behavior: 'smooth' });
  };

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Ground-truth "is the pointer actually over the carousel right now"
    // check via getBoundingClientRect + the last real mousemove position,
    // instead of a persistent isHovering ref toggled by onMouseEnter/
    // onMouseLeave. The enter/leave approach could get permanently stuck
    // true — reported directly ("doesn't automatically start until I make
    // it go forward"): if the cursor's real screen position ends up over
    // the carousel from page load (or a stray hover) without a matching
    // leave event ever firing for it (e.g. the pointer exits through
    // browser chrome, or the tab loses focus while hovered), auto-advance
    // would silently pause forever — until the user happened to move the
    // mouse to actually click something, which is what a "next" click
    // requires and therefore looked like it "fixed" it, when really the
    // click just coincided with a mousemove that finally cleared the stuck
    // flag. A pointer-position check recomputed fresh every tick can't get
    // stuck this way: whatever mouseenter/mouseleave did or didn't fire in
    // the past, this always reflects where the pointer *actually* is now.
    const handlePointerMove = (e) => {
      pointerRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('mousemove', handlePointerMove, { passive: true });

    const timer = setInterval(() => {
      const container = scrollRef.current;
      const wrapper = wrapperRef.current;
      if (!container || !container.offsetWidth || !wrapper) return;
      // Checked against the *wrapper* (track + CarouselControls below it),
      // not just the image track — hovering the prev/next arrows or the dot
      // pill should still pause auto-advance too, same as the old
      // onMouseEnter/onMouseLeave was scoped to the whole .thumbnail-carousel.
      const { x, y } = pointerRef.current;
      const rect = wrapper.getBoundingClientRect();
      const isPointerOver = x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
      if (isPointerOver) return;
      const current = Math.round(container.scrollLeft / container.offsetWidth);
      const next = (current + 1) % items.length;
      container.scrollTo({ left: next * container.offsetWidth, behavior: 'smooth' });
    }, AUTO_ADVANCE_MS);

    return () => {
      window.removeEventListener('mousemove', handlePointerMove);
      clearInterval(timer);
    };
  }, []);

  return (
    <div className="thumbnail-carousel" ref={wrapperRef}>
      <div className="thumbnail-carousel-track" ref={scrollRef}>
        {items.map((item) => {
          const isInternal = item.url.startsWith('/');
          const Wrapper = isInternal ? Link : 'a';
          const wrapperProps = isInternal
            ? { to: item.url }
            : { href: item.url, target: '_blank', rel: 'noopener noreferrer' };

          return (
            <Wrapper
              key={item.key}
              className="thumbnail-carousel-item"
              {...wrapperProps}
            >
              <img
                src={item.image}
                alt={item.title}
                style={item.imagePosition ? { objectPosition: item.imagePosition } : undefined}
              />
            </Wrapper>
          );
        })}
      </div>

      <CarouselControls
        count={items.length}
        activeIndex={activeIndex}
        dotRefs={dotRefs}
        getLabel={(i) => items[i].title}
        onDotClick={scrollToIndex}
        onPrev={() => scrollToIndex((activeIndex - 1 + items.length) % items.length)}
        onNext={() => scrollToIndex((activeIndex + 1) % items.length)}
      />
    </div>
  );
};

export default ThumbnailCarousel;
